// Consumer-portal i18n for the auth flow.
//
// Mirrors packages/admin/src/i18n/strings.tsx but with consumer-flavor brand
// strings (warm editorial — "GC Coach" wordmark, "The management coach for
// General Counsel." headline). Per the designer's spec, only the brand panel
// copy differs between admin and consumer; form-panel strings (login fields,
// buttons, validation) are identical.
//
// Source of truth: design/UI : UX v1.0/for-engineering-auth-admin-consumer/
// specs/screen-strings.md § SHARED · Brand panel · Consumer flavor.
//
// Two locales: EN + FR. Scope is intentionally narrow — only the sections
// the consumer SPA actually renders today (Chunk 1: brand + login). MFA,
// password-reset, etc. land in later chunks as the screens are built.

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

export type Lang = 'EN' | 'FR';

type StringTree = { readonly [key: string]: string | StringTree };

const en = {
  common: {
    or: 'OR',
    back: 'Back',
    close: 'Close',
    signOut: 'Sign out',
  },
  brand: {
    // Consumer-flavor chrome per designer spec § SHARED · Brand panel · Consumer flavor.
    // Warm editorial — display wordmark, no badge, dots backdrop (handled in CSS).
    wordmark: 'GC Coach',
    wordmarkSub: 'EST. MMXXVI',
    eyebrow: 'A MANAGEMENT TOOL · NOT LEGAL ADVICE',
    headline: 'The management coach for General Counsel.',
    tagline:
      'Decide, organize, prove value — without rendering legal advice. A management framework dedicated to GCs.',
    footerLeft: 'VERSION 0.5 · PILOT',
    footerRight: 'EU · FR / EN',
  },
  login: {
    eyebrow: 'SIGN IN',
    headline: 'Welcome back',
    emailLabel: 'WORK EMAIL',
    emailPlaceholder: 'florence.larrieu@acme.com',
    passwordLabel: 'PASSWORD',
    rememberDevice: 'Remember this device',
    forgotPassword: 'Forgot password?',
    submitButton: 'Sign in →',
    ssoMicrosoft: 'Continue with Microsoft',
    ssoGoogle: 'Continue with Google',
    ssoEnterprise: 'Single sign-on',
    footerLine: 'No account yet? Access to GC Coach is granted through your legal department.',
    footerLink: 'Request access',
    errorInvalidCredentials: 'That email or password didn’t work. Try again.',
    errorRateLimited: 'Too many attempts. Wait a moment, then try again.',
  },
  firstLogin: {
    eyebrow: 'WELCOME · FIRST SIGN-IN',
    headline: 'Set your password',
    body: 'Your account {email} was created by your administrator. Choose a password to finish setup.',
    newPasswordLabel: 'NEW PASSWORD',
    confirmPasswordLabel: 'CONFIRM PASSWORD',
    submitButton: 'Set password →',
    errorMismatch: 'Passwords don’t match.',
  },
  mfaPlaceholder: {
    eyebrow: 'TWO-FACTOR VERIFICATION',
    headline: 'Two-factor isn’t set up for your workspace yet',
    body: 'Contact your administrator — multi-factor sign-in for the tenant portal arrives in the next release.',
  },
  sessionExpired: {
    badge: 'SESSION EXPIRED',
    captionTemplate: '{minutes} MIN INACTIVE',
    headline: 'Sign back in to continue',
    body: 'For your security, we ask you to re-authenticate after a period of inactivity.',
    accountLabel: 'ACCOUNT',
    passwordLabel: 'PASSWORD',
    submitButton: 'Resume session →',
    signOutLink: 'Sign out instead',
    saveCaption: 'YOUR WORK IS SAVED',
    errorWrongPassword: 'That password didn’t work. Try again.',
    errorRateLimited: 'Too many attempts. Wait a moment, then try again.',
    errorGeneric: 'Couldn’t reauthenticate. Try again.',
  },
  coach: {
    legal: 'MANAGEMENT COACHING · NOT LEGAL ADVICE',
    you: 'YOU',
    author: 'GC COACH',
    composer: {
      placeholder: 'Ask the coach…',
      hint: 'Enter to send · Shift+Enter for a new line',
      memory: 'USING YOUR NOTEBOOK',
    },
    send: 'Send',
    sources: 'SOURCES',
    recall: 'Drew on {n} notes',
    'recall.open': 'OPEN NOTEBOOK →',
    loading: {
      single: 'Thinking this through…',
      stage1: 'Read your context',
      stage2: 'Searching the knowledge base',
      stage3: 'Drafting',
    },
    decline: {
      eyebrow: 'COACHING SCOPE',
    },
    fb: {
      helpful: 'Helpful',
      not: 'Not helpful',
      thanks: 'Thanks — noted',
      title: 'WHAT MISSED?',
      r1: 'Off topic',
      r2: 'Too generic',
      r3: 'Missed my context',
      note: 'Anything else? (optional)',
      send: 'Send feedback',
    },
    loadError: 'Could not load conversation history.',
    sendError: 'Could not send message. Try again.',
    srSending: 'Coach is responding',
    srMessages: '{n} messages in conversation',
    dictation: {
      button: {
        idle: 'Dictate a message',
        idleTooltip: 'Dictate — ⌘⇧M',
        idleLabel: 'DICTATE',
        live: 'Stop dictation',
        paused: 'Resume dictation',
        denied: 'Dictation unavailable — microphone blocked',
      },
      label: {
        listening: 'LISTENING',
        paused: 'PAUSED',
        blocked: 'MIC BLOCKED',
      },
      prepermission: {
        title: 'Dictate with your voice',
        body: 'Your browser will ask to use your microphone. Speech is turned into text as you talk and is never recorded or stored. When the mic is on, the whole screen is outlined in red, so you’ll always know.',
        check: 'Before you start, check who can hear you.',
        allow: 'Allow microphone',
        later: 'Not now',
      },
      denied: {
        title: 'Your microphone is blocked for this site',
        body: 'To dictate, allow the microphone in your browser, then reload the page:',
        step1: 'Click the lock icon next to the web address.',
        step2: 'Set Microphone to Allow.',
      },
      keepTyping: 'Keep typing',
      strip: {
        liveTitle: 'Microphone on',
        liveBody: 'GC Coach is listening',
        langTooltip: 'Detected language — click to lock',
        pause: 'Pause',
        discard: 'Discard',
        stop: 'Stop',
        pausedTitle: 'Microphone paused',
        pausedBody: 'nothing is being heard',
        resume: 'Resume',
        timeoutTimer: 'ends in {time}',
        timeoutBody: '5-minute limit — stop to keep what you have, or keep talking until it ends.',
        endedTitle: 'Microphone off',
        endedBody: 'your words are in the message below. Nothing was recorded.',
        endedTimeout: 'the 5-minute limit was reached. Your words are in the message below.',
        errorDrop: 'dictation stopped because the connection dropped.',
        errorMic: 'dictation stopped because your microphone stopped responding.',
      },
      error: {
        title: 'Your words so far are safe',
        body: 'Everything transcribed before the drop is in your message. Try again, or carry on typing.',
        retry: 'Try again',
      },
      sendDisabled: 'Stop dictation to send',
      toast: {
        added: 'Dictation added {n} words',
        discarded: 'Dictation discarded',
        undo: 'UNDO',
      },
      tab: {
        live: '● Listening — GC Coach',
        paused: '‖ Mic paused — GC Coach',
      },
    },
  },
  // Memory sub-keys referenced from the Coach surface before the full
  // Notebook ships (MR D). Full memory.* block lands there.
  memory: {
    src: {
      inferred: {
        badge: 'INFERRED',
      },
    },
  },
  // Projects (MR J). Per the coach-v2 strings spec.
  projects: {
    section: 'PROJECTS',
    new: '+ New project',
    empty:
      'Group related sessions — a budget cycle, a reorg, a negotiation. Sessions keep their dates; the project just files them together.',
    filter: '{project} · {n} sessions',
    back: '← Back to all sessions',
    eyebrow: 'PROJECT',
    rename: 'Rename',
    manage: 'Archive or delete…',
    desc: {
      placeholder: 'Add a one-line description',
    },
    stats: '{n} SESSIONS · LAST ACTIVE {date}',
    noSessions: 'No sessions in this project yet.',
    detail: {
      empty: {
        title: 'Nothing filed here yet',
        body:
          'Start a session while this project is selected and it will be filed here. You can also move existing sessions in from their menu.',
        cta: '+ New session in this project',
      },
    },
    composer: 'Ask about {project}…',
    move: 'Move to project',
    moveNew: '+ New project…',
    remove: 'Remove from project',
    manageTitle: 'What should happen to "{project}"?',
    archive: 'Archive',
    archiveDesc:
      'Hidden from the rail. Its {n} sessions keep the label and stay findable with "Include archived".',
    delete: 'Delete project',
    deleteDesc:
      'The project is removed. Its {n} sessions are kept and become unassigned.',
    archiveGo: 'Archive project',
    createTitle: 'New project',
    createNameLabel: 'NAME',
    createDescLabel: 'DESCRIPTION (OPTIONAL)',
    createSave: 'Create project',
    cancel: 'Cancel',
    error: {
      load: 'Could not load projects. Try again.',
      save: 'Could not save project. Try again.',
    },
  },
  // Session search (MR I) — RailSearch + SearchResults.
  search: {
    placeholder: 'Search sessions',
    results: '{n} RESULTS',
    keys: '↑↓ ENTER',
    moreMatches: '+{n} more matches in this session',
    searching: 'SEARCHING…',
    loadingMore: 'LOADING MORE…',
    archivedNote: 'Archived projects are not searched.',
    includeArchived: 'Include archived',
    none: {
      title: 'No sessions match "{query}"',
      body: 'Try fewer or different words.',
    },
    error: {
      title: 'Search failed',
      body: 'Something went wrong on our end. Please try again.',
    },
    opened: 'OPENED FROM SEARCH · JUMPED TO THE FIRST MATCH',
  },
  // Reasoning visibility (MR H) — SourcesLine, SourceTypeBadge,
  // ConsultedPanel. Per the designer v2 strings spec.
  sources: {
    line: 'Drew on {n} sources',
    lineOne: 'Drew on 1 source',
    none: 'No knowledge-base sources for this reply.',
    show: 'SHOW ▾',
    hide: 'HIDE ▴',
    type: {
      qa_pair: 'Guidance',
      framework: 'Framework',
      case_study: 'Case study',
      document_excerpt: 'Book excerpt',
    },
    usedIn: 'Used in paragraph {n} →',
    usedPlaceholder: '',
    notCited: '{n} more consulted, not cited',
    confidence: {
      high: 'CLOSE MATCH',
      medium: 'RELATED',
    },
  },
  // History / Sessions rail (MR C). Full history.* block.
  history: {
    new: '+ New session',
    untitled: 'New session · {date}',
    filter: {
      all: 'All',
    },
    framework: {
      badge: 'FRAMEWORK',
    },
    group: {
      pinned: 'PINNED',
      today: 'TODAY',
      yesterday: 'YESTERDAY',
      previous7: 'PREVIOUS 7 DAYS',
      previous30: 'PREVIOUS 30 DAYS',
      // Kept for back-compat with any dangling consumer; new UI uses the
      // day-based buckets above.
      week: 'THIS WEEK',
      last: 'LAST WEEK',
    },
    rail: {
      empty:
        'Your sessions will appear here. Each one is a piece of work you can come back to.',
    },
    welcome: {
      title: 'What are you working on this week?',
      body:
        'Each session is a piece of work you can come back to. The coach keeps notes on your context, so you won’t have to explain your team twice.',
    },
    resume: {
      eyebrow: 'PICK UP WHERE YOU LEFT OFF',
      go: 'Resume',
      other: 'Start something new',
    },
    rename: {
      hint: 'Click to rename',
      save: 'Save',
      cancel: 'Cancel',
    },
    // Row-level kebab menu (⋮). Backs the SessionMenu component used by
    // both the rail row and search-result rows.
    actions: {
      menuLabel: 'Session actions',
      pin: 'Pin to top',
      unpin: 'Unpin',
      archive: 'Archive',
      unarchive: 'Unarchive',
      archived: 'ARCHIVED',
      back: 'Back',
      noProjects: 'No projects yet.',
    },
    mobileToggle: 'Sessions',
    mobileClose: 'Close',
  },
  onboarding: {
    welcome: {
      eyebrow: 'WELCOME',
      headlineNamed: 'Welcome to GC Coach, {firstName}.',
      headlineFallback: 'Welcome to GC Coach.',
      body: 'Your AI strategic coach for running the legal function. Let’s set up your workspace — it takes about two minutes.',
      continueCta: 'Continue →',
    },
    contract: {
      stepBadge: 'STEP 1 · REQUIRED',
      eyebrow: 'COACHING CONTRACT',
      headline: 'Before you begin — what GC Coach is, and isn’t.',
      checkboxLabel:
        'I understand GC Coach provides management and strategic coaching — not legal advice — and I accept the Coaching Contract.',
      requiredHint: 'NO SKIP — REQUIRED',
      acceptCta: 'Accept & continue →',
      reAcceptEyebrow: 'COACHING CONTRACT · UPDATED',
      reAcceptHeadline: 'We’ve updated the Coaching Contract',
      whatChangedLabel: 'What changed',
      reAcceptCheckboxLabel:
        'I have reviewed and accept the updated Coaching Contract.',
      requiredToContinue: 'REQUIRED TO CONTINUE',
      reAcceptCta: 'Accept & continue',
      loading: 'Loading contract…',
      loadError: 'Could not load the Coaching Contract. Please try again.',
      acceptError: 'Could not record acceptance. Please try again.',
    },
    profile: {
      stepBadge: 'STEP 2 · OPTIONAL',
      title: 'Tell us about your work.',
      intro:
        'This sharpens your coaching and benchmarks. You can skip and complete it later in settings.',
      roleTitleLabel: 'ROLE TITLE',
      roleTitlePlaceholder: 'General Counsel, Head of Legal…',
      yearsExperienceLabel: 'YEARS OF EXPERIENCE',
      teamSizeLabel: 'TEAM SIZE',
      teamSizePlaceholder: 'Number of lawyers',
      industryLabel: 'INDUSTRY SECTOR',
      industryPlaceholder: 'e.g. SaaS, Banking, Pharma',
      jurisdictionsLabel: 'PRIMARY JURISDICTIONS',
      jurisdictionsPlaceholder: 'FR, IE, US…',
      jurisdictionsHint: 'Two-letter codes, comma-separated',
      skipCta: 'Skip for now',
      continueCta: 'Continue →',
    },
    firstFramework: {
      stepBadge: 'STEP 3 · SUGGESTED',
      title: 'Based on your profile, we recommend:',
      intro:
        'Activate this framework to start running it as a guided programme — or browse the library to choose your own.',
      suggestedDomain: 'LEGAL OPERATIONS',
      suggestedName: 'Contract Lifecycle Maturity',
      suggestedOneLiner:
        'Build a structured contracting process: intake, review, signature, and retention. The fastest path to operational maturity for most legal teams.',
      activateCta: 'Activate now',
      browseCta: 'Browse all frameworks',
      skipCta: 'Skip for now',
    },
    done: {
      eyebrow: 'ALL SET',
      title: 'You’re all set. Welcome aboard.',
      body: 'Your workspace is ready. Start by activating a framework, or ask the coach a question to explore what GC Coach can do.',
      askCoachCta: 'Ask the Coach',
      dashboardCta: 'Go to dashboard →',
    },
  },
  shell: {
    // Chrome (sidebar + topbar + UserMenu)
    sidebarLabel: 'Tenant portal navigation',
    sidebarNavLabel: 'Workspace',
    brandWordmark: 'GC Coach',
    brandSub: 'TENANT PORTAL',
    workspaceLabel: 'WORKSPACE',
    envTrailing: 'EU · v0.5',
    tenantNameFallback: 'Your workspace',
    openSidebar: 'Open navigation',
    notificationsBellLabel: 'Notifications',
    userMenuLoading: 'Loading…',
    userMenuRoleTenantAdmin: 'TENANT ADMIN',
    userMenuProfile: 'Profile',
    userMenuPreferences: 'Preferences',
    userMenuSignOut: 'Sign out',
    // Sign-out confirm dialog
    signOutTitle: 'Sign out?',
    signOutBody: 'You’ll return to the sign-in screen.',
    signOutCancel: 'Cancel',
    signOutConfirm: 'Sign out',
    // Nav item labels
    nav: {
      coach: 'Coach',
      dashboard: 'Dashboard',
      frameworks: 'Frameworks',
      notifications: 'Notifications',
      adoption: 'Adoption',
    },
    // Shell-level state copy
    stateLoading: 'Loading',
    state401Title: 'Please sign in again.',
    state401Body:
      'Your session has expired for security. Sign in again to return to your workspace.',
    state401Button: 'Sign in →',
    stateNetworkTitle: 'Connection lost',
    stateNetworkBody:
      'Your connection dropped. Check your network and try again.',
    stateNetworkButton: 'Retry',
    // /coach empty state — V1 placeholder, full surface lands in C3
    coach: {
      eyebrow: 'COACH',
      headline: 'Your strategic coach is almost ready.',
      body: 'Ask about legal operations, frameworks, or strategy. The full conversational surface arrives with our next release.',
    },
    // /dashboard empty state
    dashboard: {
      eyebrow: 'WELCOME TO GC COACH',
      headlineNamed: 'Your management workspace is ready, {firstName}.',
      headlineFallback: 'Your management workspace is ready.',
      body: 'GC Coach helps you run the legal function as a series of guided frameworks — decide, organize, and prove value. Activate your first framework to begin.',
      primaryCta: 'Activate your first framework →',
      secondaryCta: 'Take the tour',
    },
    // /frameworks empty state + 8-domain teaser
    frameworks: {
      eyebrow: 'FRAMEWORKS LIBRARY',
      headline: 'Frameworks are coming to your workspace.',
      body: 'Guided, structured programmes across eight domains of legal-department management. Your first framework will appear here shortly.',
      comingSoon: 'COMING SOON',
      d1Label: 'Legal Operations',
      d2Label: 'Contracting',
      d3Label: 'Compliance',
      d4Label: 'Disputes & Crisis',
      d5Label: 'Knowledge & Tools',
      d6Label: 'People & Vendors',
      d7Label: 'Risk & Governance',
      d8Label: 'Strategy & Value',
    },
    // /notifications empty state
    notifications: {
      eyebrow: 'NOTIFICATIONS',
      headline: 'You’re all caught up.',
      body: 'No notifications yet. We’ll let you know when a framework is published, a phase deadline approaches, or your team needs you.',
    },
    // /adoption empty state — tenant_admin only
    adoption: {
      eyebrow: 'TEAM ADOPTION',
      headline: 'Invite your team to see adoption.',
      body: 'Adoption shows how your team engages with frameworks — progress, activity, and momentum. It comes to life once colleagues join your workspace.',
      primaryCta: 'Invite team members →',
    },
  },
  // Framework consumer surfaces (Library · Preview · Activation · Workspace).
  // Spec: design/UI : UX v1.0/for-designer-frameworks-v2-model-and-consumer/
  //       specs/consumer-frameworks-strings.md
  frameworks: {
    library: {
      breadcrumb: 'FRAMEWORKS',
      title: 'Frameworks',
      lead: 'Structured playbooks that take your department from where it is to where it needs to be — each one produces tangible deliverables and advances your maturity in a domain.',
      filterAll: 'All',
      searchPlaceholder: 'Search frameworks…',
      bandRecommended: 'RECOMMENDED NEXT',
      bandRecommendedNote: 'targets the level above where you are today',
      bandAll: 'ALL FRAMEWORKS',
      ribbonRecommended: 'RECOMMENDED',
      ribbonInProgressTemplate: 'IN PROGRESS · {n}%',
      badgeAlreadyThere: 'ALREADY THERE',
      badgeAmbitious: 'AMBITIOUS',
      ambitiousNote: 'Two levels ahead — consider a “defined” framework first.',
      metricDeliverables: ' deliverables',
      metricPhases: ' phases',
      metricActivities: ' activities',
      metricMonths: ' months',
      emptyInDomainTitle: 'No frameworks in this domain yet',
      emptyInDomainBody:
        'The content team is authoring more. In the meantime, browse the other domains — or ask the Coach what to prioritise.',
      emptyNoneAtAllTitle: 'No frameworks published yet',
      emptyNoneAtAllBody:
        'The content team is finishing the first batch. We will drop a note here as soon as a framework lands.',
      emptyClearFilter: 'Clear domain filter',
      errorGeneric:
        'We could not load the library just now. Refresh the page or try again in a moment.',
    },
    domain: {
      D1_strategy_org: 'D1 — Strategy & Org',
      D2_external_counsel: 'D2 — External Counsel',
      D3_legal_ops_tech: 'D3 — Legal Ops & Tech',
      D4_team_dev: 'D4 — Team Development',
      D5_kpis_performance: 'D5 — KPIs & Performance',
      D6_budget_finance: 'D6 — Budget & Finance',
      D7_stakeholder_comms: 'D7 — Stakeholder Comms',
      D8_governance_risk: 'D8 — Governance & Risk',
    },
    maturity: {
      initial: 'initial',
      developing: 'developing',
      established: 'established',
      advanced: 'advanced',
      optimized: 'optimized',
    },
    duration: {
      lt_1_month: '< 1 month',
      '1_3_months': '1–3 months',
      '3_6_months': '3–6 months',
      '6_9_months': '6–9 months',
      '9_12_months': '9–12 months',
      '12_18_months': '12–18 months',
      '18_24_months': '18–24 months',
    },
    workspace: {
      activitiesLabel: 'ACTIVITIES',
      askCoachCta: 'Ask the Coach',
      askCoachAbout: 'Ask the Coach about this',
      askCoachOpening: 'Opening the coach…',
      askCoachError: 'Could not open the coach. Try again.',
      tabFocus: 'Focus',
      tabTimeline: 'Timeline',
      tabDeliverables: 'Deliverables',
      doThisNext: 'DO THIS NEXT',
      openActivity: 'Open activity',
      allDoneTitle: 'All done.',
      allDoneBody:
        'Every activity on this framework is complete. Head to Deliverables to confirm your maturity shift when you are ready.',
      nothingReadyTitle: 'Nothing is ready right now',
      nothingReadyBody:
        'Every un-finished activity is waiting on a dependency. Clear a blocker, or revisit the plan to unstick things.',
      driftHeader: 'One or more activities are past their planned window',
      driftPlannedTemplate: 'planned to finish on {date}',
      awaitingReviewLabel: 'AWAITING YOUR REVIEW',
      readyLabel: 'READY WHEN YOU ARE',
      blockedLabel: 'WAITING ON SOMETHING ELSE',
      needsPrefix: 'needs',
      timelinePlaceholderTitle: 'Timeline is coming in the next release',
      timelinePlaceholderBody:
        'A month-by-month Gantt-style view of every activity, phase group, drift markers, and dependency edges. Shipping in V1 proper.',
      deliverablesPlaceholderTitle: 'Deliverables tab is coming in the next release',
      deliverablesPlaceholderBody:
        'A per-deliverable state view with upload, confirm, and (V1.x) coach evaluation. The underlying data is already captured on your activities; this tab surfaces it.',
      notFoundTitle: 'Instance not found',
      notFoundBody:
        'This running instance may have been removed or the link is incorrect. Head back to your library.',
      waiting: 'WAITING',
      markStarted: 'Mark started',
      markComplete: 'Mark complete',
      reopen: 'Re-open',
      closeDrawer: 'Close',
      state: {
        not_started: 'Not started',
        in_progress: 'In progress',
        awaiting_review: 'Awaiting review',
        complete: 'Complete',
      },
      durationLabel: 'DURATION',
      dependsOnLabel: 'DEPENDS ON',
      dependsOnNothing: 'nothing',
      stakeholdersLabel: 'STAKEHOLDERS',
      referenceLabel: 'REFERENCE MATERIAL',
      producesDeliverableLabel: 'PRODUCES A DELIVERABLE',
      templateNote:
        'Fill this in, then upload it back — that is how the deliverable gets made.',
      logLabel: 'ACTIVITY LOG',
      logEmpty: 'No notes yet.',
      addNotePlaceholder: 'Add a note…',
      addNoteSubmit: 'Add note',
      blockedBlockPrefix: 'Waiting on',
      blockedBlockSuffix:
        'You can still start — the plan just assumes this order.',
    },
    activation: {
      stepLabel: 'STEP 1 OF 2',
      titleTemplate: 'Start {name}?',
      summaryDeliverables: 'DELIVERABLES',
      summaryMaturity: 'MATURITY',
      summaryDuration: 'DURATION',
      nameLabel: 'INSTANCE NAME',
      startDateLabel: 'START DATE',
      startDateNote: 'Drives the timeline. Change it any time.',
      adjustDurationsLabel: 'ADJUST DURATIONS',
      adjustDurationsNote:
        'Defaults come from the framework author. Yours override them.',
      criticalPathNote:
        'Critical path assumes activities on independent branches run in parallel. Push the estimate up if that is not realistic for your team.',
      defaultTag: 'default',
      ackLabel:
        'I have reviewed who needs to be involved — including Board, CEO and CFO time.',
      cancel: 'Cancel',
      submit: 'Activate',
      submitting: 'Activating…',
      alreadyRunningTitle: 'Already running',
      alreadyRunningBody:
        'This framework already has a running instance for your organization. Open where you left off, or cancel and look elsewhere.',
      alreadyRunningContinue: 'Open your running instance',
      errorTitle: 'Could not load this framework',
      errorGeneric:
        'Activation did not go through. Try again in a moment, or contact support if it keeps failing.',
    },
    preview: {
      backToLibrary: 'All frameworks',
      notFoundTitle: 'Framework not found',
      notFoundBody:
        'This framework may have been retired or the link is incorrect. Try the library.',
      ctaStartFramework: 'Start this framework',
      ctaResumeInstance: 'Open your running instance',
      ctaAskCoach: 'Ask the Coach about this',
      ctaFooterNote: 'You can adjust durations and start date next.',
      whatThisMovesLabel: 'WHAT THIS MOVES',
      whatThisMovesBodyTemplate:
        'You are at {current} in {domain}. Completing this framework advances you to {target} — once you confirm the evidence supports it.',
      whatThisMovesBodyNoTarget:
        'The content team hasn’t set a maturity target on this framework yet. Running it is still valuable; the maturity advancement claim will land once a target is published.',
      currentUnknown: 'unknown level',
      timeLabel: 'TIME',
      timeNote: 'you can adjust every activity',
      timeUnknown: 'duration TBD',
      deliverablesEyebrow: 'WHAT YOU WALK AWAY WITH',
      deliverablesTitleTemplate: '{n} deliverables',
      showRubric: 'What “good” looks like →',
      hideRubric: 'Hide the rubric',
      theWorkEyebrow: 'THE WORK',
      theWorkNote: 'open any activity — full detail, before you commit',
      phaseLabel: 'PHASE',
      afterLabel: 'after',
      producesDeliverable: 'PRODUCES A DELIVERABLE',
      stakeholdersLabel: 'WHO YOU WILL NEED',
      stakeholdersBody:
        'Across all activities. Senior-stakeholder time (Board, CEO, CFO) is the most common reason a strategic framework stalls — worth checking availability before you start.',
    },
  },
} satisfies StringTree;

const fr: typeof en = {
  common: {
    or: 'OU',
    back: 'Retour',
    close: 'Fermer',
    signOut: 'Se déconnecter',
  },
  brand: {
    wordmark: 'GC Coach',
    wordmarkSub: 'EST. MMXXVI',
    eyebrow: 'L’OUTIL DE PILOTAGE · PAS D’AVIS JURIDIQUE',
    headline: 'Le coach de pilotage pour les directions juridiques.',
    tagline:
      'Décider, organiser, démontrer la valeur — sans rendre d’avis juridique. Un cadre de management dédié aux GC.',
    footerLeft: 'VERSION 0.5 · PILOTE',
    footerRight: 'EU · FR / EN',
  },
  login: {
    eyebrow: 'CONNEXION',
    headline: 'Bon retour',
    emailLabel: 'EMAIL PROFESSIONNEL',
    emailPlaceholder: 'florence.larrieu@acme.com',
    passwordLabel: 'MOT DE PASSE',
    rememberDevice: 'Se souvenir de cet appareil',
    forgotPassword: 'Mot de passe oublié ?',
    submitButton: 'Se connecter →',
    ssoMicrosoft: 'Continuer avec Microsoft',
    ssoGoogle: 'Continuer avec Google',
    ssoEnterprise: 'Authentification unique',
    footerLine: 'Pas encore de compte ? L’accès à GC Coach se fait via votre direction juridique.',
    footerLink: 'Demander l’accès',
    errorInvalidCredentials: 'Cet email ou ce mot de passe n’a pas fonctionné. Réessayez.',
    errorRateLimited: 'Trop de tentatives. Patientez un instant, puis réessayez.',
  },
  firstLogin: {
    eyebrow: 'BIENVENUE · PREMIÈRE CONNEXION',
    headline: 'Définissez votre mot de passe',
    body: 'Votre compte {email} a été créé par votre administrateur. Choisissez un mot de passe pour finaliser la configuration.',
    newPasswordLabel: 'NOUVEAU MOT DE PASSE',
    confirmPasswordLabel: 'CONFIRMER LE MOT DE PASSE',
    submitButton: 'Définir le mot de passe →',
    errorMismatch: 'Les mots de passe ne correspondent pas.',
  },
  mfaPlaceholder: {
    eyebrow: 'VÉRIFICATION À DEUX FACTEURS',
    headline: 'La vérification à deux facteurs n’est pas encore configurée',
    body: 'Contactez votre administrateur — la connexion multi-facteurs pour le portail client arrive à la prochaine version.',
  },
  sessionExpired: {
    badge: 'SESSION EXPIRÉE',
    captionTemplate: 'INACTIF DEPUIS {minutes} MIN',
    headline: 'Reconnectez-vous pour continuer',
    body: 'Pour votre sécurité, nous demandons une nouvelle authentification après une période d’inactivité.',
    accountLabel: 'COMPTE',
    passwordLabel: 'MOT DE PASSE',
    submitButton: 'Reprendre la session →',
    signOutLink: 'Se déconnecter à la place',
    saveCaption: 'TOUT TRAVAIL EN COURS EST SAUVEGARDÉ',
    errorWrongPassword: 'Ce mot de passe n’a pas fonctionné. Réessayez.',
    errorRateLimited: 'Trop de tentatives. Patientez un instant, puis réessayez.',
    errorGeneric: 'Impossible de se reconnecter. Réessayez.',
  },
  coach: {
    legal: 'COACHING DE PILOTAGE · PAS DE CONSEIL JURIDIQUE',
    you: 'VOUS',
    author: 'GC COACH',
    composer: {
      placeholder: 'Posez votre question au coach…',
      hint: 'Entrée pour envoyer · Maj+Entrée pour une ligne',
      memory: 'UTILISE VOTRE CARNET',
    },
    send: 'Envoyer',
    sources: 'SOURCES',
    recall: 'S’appuie sur {n} notes',
    'recall.open': 'OUVRIR LE CARNET →',
    loading: {
      single: 'Je réfléchis…',
      stage1: 'Contexte lu',
      stage2: 'Recherche dans la base',
      stage3: 'Rédaction',
    },
    decline: {
      eyebrow: 'PÉRIMÈTRE DU COACHING',
    },
    fb: {
      helpful: 'Utile',
      not: 'Pas utile',
      thanks: 'Merci, c’est noté',
      title: 'QU’EST-CE QUI N’ALLAIT PAS ?',
      r1: 'Hors sujet',
      r2: 'Trop générique',
      r3: 'Mon contexte ignoré',
      note: 'Autre chose ? (facultatif)',
      send: 'Envoyer',
    },
    loadError: 'Impossible de charger l’historique.',
    sendError: 'Impossible d’envoyer le message. Réessayez.',
    srSending: 'Le coach répond',
    srMessages: '{n} messages dans la conversation',
    dictation: {
      button: {
        idle: 'Dicter un message',
        idleTooltip: 'Dicter — ⌘⇧M',
        idleLabel: 'DICTER',
        live: 'Arrêter la dictée',
        paused: 'Reprendre la dictée',
        denied: 'Dictée indisponible — micro bloqué',
      },
      label: {
        listening: 'ÉCOUTE',
        paused: 'EN PAUSE',
        blocked: 'MICRO BLOQUÉ',
      },
      prepermission: {
        title: 'Dictez avec votre voix',
        body: 'Votre navigateur va demander l’accès au micro. La parole est transcrite en direct et n’est jamais enregistrée ni conservée. Quand le micro est actif, tout l’écran est encadré de rouge : vous le saurez toujours.',
        check: 'Avant de commencer, vérifiez qui peut vous entendre.',
        allow: 'Autoriser le micro',
        later: 'Plus tard',
      },
      denied: {
        title: 'Le micro est bloqué pour ce site',
        body: 'Pour dicter, autorisez le micro dans votre navigateur, puis rechargez la page :',
        step1: 'Cliquez sur le cadenas à côté de l’adresse.',
        step2: 'Réglez Microphone sur Autoriser.',
      },
      keepTyping: 'Continuer au clavier',
      strip: {
        liveTitle: 'Micro activé',
        liveBody: 'GC Coach vous écoute',
        langTooltip: 'Langue détectée — cliquer pour verrouiller',
        pause: 'Pause',
        discard: 'Annuler',
        stop: 'Arrêter',
        pausedTitle: 'Micro en pause',
        pausedBody: 'rien n’est capté',
        resume: 'Reprendre',
        timeoutTimer: 'fin dans {time}',
        timeoutBody: 'Limite de 5 minutes — arrêtez pour garder le texte, ou continuez jusqu’à la fin.',
        endedTitle: 'Micro désactivé',
        endedBody: 'votre texte est dans le message ci-dessous. Rien n’a été enregistré.',
        endedTimeout: 'la limite de 5 minutes est atteinte. Votre texte est dans le message ci-dessous.',
        errorDrop: 'la dictée s’est arrêtée, la connexion a été perdue.',
        errorMic: 'la dictée s’est arrêtée, votre micro ne répond plus.',
      },
      error: {
        title: 'Votre texte est conservé',
        body: 'Tout ce qui a été transcrit avant la coupure est dans votre message. Réessayez, ou continuez au clavier.',
        retry: 'Réessayer',
      },
      sendDisabled: 'Arrêtez la dictée pour envoyer',
      toast: {
        added: 'Dictée : {n} mots ajoutés',
        discarded: 'Dictée annulée',
        undo: 'ANNULER',
      },
      tab: {
        live: '● Écoute — GC Coach',
        paused: '‖ Micro en pause — GC Coach',
      },
    },
  },
  memory: {
    src: {
      inferred: {
        badge: 'DÉDUIT',
      },
    },
  },
  projects: {
    section: 'PROJETS',
    new: '+ Nouveau projet',
    empty:
      'Regroupez les sessions liées — un cycle budgétaire, une réorganisation, une négociation. Les sessions gardent leur date ; le projet les classe ensemble.',
    filter: '{project} · {n} sessions',
    back: '← Retour à toutes les sessions',
    eyebrow: 'PROJET',
    rename: 'Renommer',
    manage: 'Archiver ou supprimer…',
    desc: {
      placeholder: 'Ajoutez une description d’une ligne',
    },
    stats: '{n} SESSIONS · DERNIÈRE ACTIVITÉ {date}',
    noSessions: 'Aucune session dans ce projet.',
    detail: {
      empty: {
        title: 'Rien de classé ici pour l’instant',
        body:
          'Démarrez une session pendant que ce projet est sélectionné : elle y sera classée. Vous pouvez aussi déplacer des sessions existantes depuis leur menu.',
        cta: '+ Nouvelle session dans ce projet',
      },
    },
    composer: 'Une question sur {project}…',
    move: 'Déplacer vers un projet',
    moveNew: '+ Nouveau projet…',
    remove: 'Retirer du projet',
    manageTitle: 'Que faire de « {project} » ?',
    archive: 'Archiver',
    archiveDesc:
      'Masqué de la liste. Ses {n} sessions gardent l’étiquette et restent accessibles via « Inclure les archives ».',
    delete: 'Supprimer le projet',
    deleteDesc:
      'Le projet est supprimé. Ses {n} sessions sont conservées, sans projet.',
    archiveGo: 'Archiver le projet',
    createTitle: 'Nouveau projet',
    createNameLabel: 'NOM',
    createDescLabel: 'DESCRIPTION (FACULTATIVE)',
    createSave: 'Créer le projet',
    cancel: 'Annuler',
    error: {
      load: 'Impossible de charger les projets. Réessayez.',
      save: 'Impossible d’enregistrer. Réessayez.',
    },
  },
  search: {
    placeholder: 'Rechercher dans les sessions',
    results: '{n} RÉSULTATS',
    keys: '↑↓ ENTRÉE',
    moreMatches: '+{n} autres occurrences dans cette session',
    searching: 'RECHERCHE…',
    loadingMore: 'CHARGEMENT…',
    archivedNote: 'Les projets archivés ne sont pas inclus.',
    includeArchived: 'Inclure les archives',
    none: {
      title: 'Aucune session ne correspond à « {query} »',
      body: 'Essayez moins de mots, ou d’autres.',
    },
    error: {
      title: 'La recherche a échoué',
      body: 'Une erreur s’est produite de notre côté. Réessayez.',
    },
    opened: 'OUVERT DEPUIS LA RECHERCHE · PREMIÈRE OCCURRENCE',
  },
  sources: {
    line: 'S’appuie sur {n} sources',
    lineOne: 'S’appuie sur 1 source',
    none: 'Aucune source de la base pour cette réponse.',
    show: 'AFFICHER ▾',
    hide: 'MASQUER ▴',
    type: {
      qa_pair: 'Recommandation',
      framework: 'Cadre',
      case_study: 'Cas pratique',
      document_excerpt: 'Extrait d’ouvrage',
    },
    usedIn: 'Utilisée au paragraphe {n} →',
    usedPlaceholder: '',
    notCited: '{n} autres consultées, non citées',
    confidence: {
      high: 'FORTE PERTINENCE',
      medium: 'LIÉE',
    },
  },
  history: {
    new: '+ Nouvelle session',
    untitled: 'Nouvelle session · {date}',
    filter: {
      all: 'Toutes',
    },
    framework: {
      badge: 'CADRE',
    },
    group: {
      pinned: 'ÉPINGLÉES',
      today: 'AUJOURD’HUI',
      yesterday: 'HIER',
      previous7: '7 DERNIERS JOURS',
      previous30: '30 DERNIERS JOURS',
      week: 'CETTE SEMAINE',
      last: 'SEMAINE DERNIÈRE',
    },
    rail: {
      empty:
        'Vos sessions apparaîtront ici. Chacune est un travail sur lequel revenir.',
    },
    welcome: {
      title: 'Sur quoi travaillez-vous cette semaine ?',
      body:
        'Chaque session est un travail sur lequel revenir. Le coach garde des notes sur votre contexte : vous n’aurez pas à présenter votre équipe deux fois.',
    },
    resume: {
      eyebrow: 'REPRENDRE OÙ VOUS EN ÉTIEZ',
      go: 'Reprendre',
      other: 'Démarrer autre chose',
    },
    rename: {
      hint: 'Cliquer pour renommer',
      save: 'Enregistrer',
      cancel: 'Annuler',
    },
    actions: {
      menuLabel: 'Actions sur la session',
      pin: 'Épingler en haut',
      unpin: 'Désépingler',
      archive: 'Archiver',
      unarchive: 'Désarchiver',
      archived: 'ARCHIVÉE',
      back: 'Retour',
      noProjects: 'Aucun projet pour le moment.',
    },
    mobileToggle: 'Sessions',
    mobileClose: 'Fermer',
  },
  onboarding: {
    welcome: {
      eyebrow: 'BIENVENUE',
      headlineNamed: 'Bienvenue sur GC Coach, {firstName}.',
      headlineFallback: 'Bienvenue sur GC Coach.',
      body: 'Votre coach stratégique par IA pour piloter la fonction juridique. Configurons votre espace — environ deux minutes.',
      continueCta: 'Continuer →',
    },
    contract: {
      stepBadge: 'ÉTAPE 1 · REQUIS',
      eyebrow: 'CONTRAT DE COACHING',
      headline: 'Avant de commencer — ce qu’est GC Coach, et ce qu’il n’est pas.',
      checkboxLabel:
        'Je comprends que GC Coach fournit du coaching managérial et stratégique — pas un avis juridique — et j’accepte le Contrat de Coaching.',
      requiredHint: 'SANS SAUT — REQUIS',
      acceptCta: 'Accepter & continuer →',
      reAcceptEyebrow: 'CONTRAT DE COACHING · MIS À JOUR',
      reAcceptHeadline: 'Nous avons mis à jour le Contrat de Coaching',
      whatChangedLabel: 'Modifications',
      reAcceptCheckboxLabel:
        'J’ai relu et j’accepte le Contrat de Coaching mis à jour.',
      requiredToContinue: 'REQUIS POUR CONTINUER',
      reAcceptCta: 'Accepter & continuer',
      loading: 'Chargement du contrat…',
      loadError: 'Impossible de charger le Contrat de Coaching. Réessayez.',
      acceptError: 'Impossible d’enregistrer l’acceptation. Réessayez.',
    },
    profile: {
      stepBadge: 'ÉTAPE 2 · FACULTATIF',
      title: 'Parlez-nous de votre activité.',
      intro:
        'Cela améliore votre coaching et vos benchmarks. Vous pouvez passer et compléter plus tard dans les paramètres.',
      roleTitleLabel: 'INTITULÉ DU POSTE',
      roleTitlePlaceholder: 'Directeur juridique, Head of Legal…',
      yearsExperienceLabel: 'ANNÉES D’EXPÉRIENCE',
      teamSizeLabel: 'TAILLE D’ÉQUIPE',
      teamSizePlaceholder: 'Nombre d’avocats',
      industryLabel: 'SECTEUR',
      industryPlaceholder: 'ex. SaaS, Banque, Pharma',
      jurisdictionsLabel: 'JURIDICTIONS PRINCIPALES',
      jurisdictionsPlaceholder: 'FR, IE, US…',
      jurisdictionsHint: 'Codes à deux lettres, séparés par des virgules',
      skipCta: 'Passer',
      continueCta: 'Continuer →',
    },
    firstFramework: {
      stepBadge: 'ÉTAPE 3 · SUGGÉRÉ',
      title: 'D’après votre profil, nous recommandons :',
      intro:
        'Activez ce framework pour le piloter comme un programme guidé — ou parcourez la bibliothèque pour choisir.',
      suggestedDomain: 'PILOTAGE JURIDIQUE',
      suggestedName: 'Maturité du Cycle Contractuel',
      suggestedOneLiner:
        'Structurez votre processus contractuel : intake, revue, signature, conservation. La voie la plus rapide vers la maturité opérationnelle.',
      activateCta: 'Activer maintenant',
      browseCta: 'Voir tous les frameworks',
      skipCta: 'Passer pour l’instant',
    },
    done: {
      eyebrow: 'C’EST PRÊT',
      title: 'Tout est prêt. Bienvenue à bord.',
      body: 'Votre espace est prêt. Activez un framework, ou posez une question au coach pour explorer GC Coach.',
      askCoachCta: 'Demander au Coach',
      dashboardCta: 'Aller au tableau de bord →',
    },
  },
  shell: {
    sidebarLabel: 'Navigation du portail tenant',
    sidebarNavLabel: 'Espace',
    brandWordmark: 'GC Coach',
    brandSub: 'PORTAIL TENANT',
    workspaceLabel: 'ESPACE',
    envTrailing: 'EU · v0.5',
    tenantNameFallback: 'Votre espace de travail',
    openSidebar: 'Ouvrir la navigation',
    notificationsBellLabel: 'Notifications',
    userMenuLoading: 'Chargement…',
    userMenuRoleTenantAdmin: 'ADMIN TENANT',
    userMenuProfile: 'Profil',
    userMenuPreferences: 'Préférences',
    userMenuSignOut: 'Se déconnecter',
    signOutTitle: 'Se déconnecter ?',
    signOutBody: 'Vous reviendrez à l’écran de connexion.',
    signOutCancel: 'Annuler',
    signOutConfirm: 'Se déconnecter',
    nav: {
      coach: 'Coach',
      dashboard: 'Tableau de bord',
      frameworks: 'Frameworks',
      notifications: 'Notifications',
      adoption: 'Adoption',
    },
    stateLoading: 'Chargement',
    state401Title: 'Veuillez vous reconnecter.',
    state401Body:
      'Votre session a expiré pour des raisons de sécurité. Reconnectez-vous pour revenir à votre espace.',
    state401Button: 'Se connecter →',
    stateNetworkTitle: 'Connexion perdue',
    stateNetworkBody:
      'Votre connexion a été interrompue. Vérifiez votre réseau, puis réessayez.',
    stateNetworkButton: 'Réessayer',
    coach: {
      eyebrow: 'COACH',
      headline: 'Votre coach stratégique arrive bientôt.',
      body: 'Posez des questions sur le pilotage juridique, les frameworks ou la stratégie. La conversation complète arrive dans notre prochaine version.',
    },
    dashboard: {
      eyebrow: 'BIENVENUE SUR GC COACH',
      headlineNamed: 'Votre espace de pilotage est prêt, {firstName}.',
      headlineFallback: 'Votre espace de pilotage est prêt.',
      body: 'GC Coach vous aide à piloter la direction juridique via des frameworks guidés — décider, organiser, démontrer la valeur. Activez votre premier framework pour commencer.',
      primaryCta: 'Activez votre premier framework →',
      secondaryCta: 'Visite guidée',
    },
    frameworks: {
      eyebrow: 'BIBLIOTHÈQUE DE FRAMEWORKS',
      headline: 'Les frameworks arrivent dans votre espace.',
      body: 'Des programmes guidés et structurés couvrant huit domaines du pilotage juridique. Votre premier framework apparaîtra ici prochainement.',
      comingSoon: 'BIENTÔT',
      d1Label: 'Pilotage juridique',
      d2Label: 'Contractualisation',
      d3Label: 'Conformité',
      d4Label: 'Litiges & Crise',
      d5Label: 'Savoir & Outils',
      d6Label: 'Équipes & Prestataires',
      d7Label: 'Risque & Gouvernance',
      d8Label: 'Stratégie & Valeur',
    },
    notifications: {
      eyebrow: 'NOTIFICATIONS',
      headline: 'Vous êtes à jour.',
      body: 'Aucune notification pour l’instant. Nous vous préviendrons lorsqu’un framework est publié, qu’une échéance approche, ou que votre équipe a besoin de vous.',
    },
    adoption: {
      eyebrow: 'ADOPTION DE L’ÉQUIPE',
      headline: 'Invitez votre équipe pour suivre l’adoption.',
      body: 'L’adoption montre comment votre équipe utilise les frameworks — progression, activité et dynamique. Elle prend vie dès que des collègues rejoignent votre espace.',
      primaryCta: 'Inviter des membres →',
    },
  },
  frameworks: {
    library: {
      breadcrumb: 'CADRES',
      title: 'Cadres',
      lead: 'Des playbooks structurés qui mènent votre département d’où il est à où il doit aller — chacun produit des livrables tangibles et fait progresser votre maturité dans un domaine.',
      filterAll: 'Tous',
      searchPlaceholder: 'Rechercher un cadre…',
      bandRecommended: 'RECOMMANDÉS',
      bandRecommendedNote: 'visent le niveau au-dessus de votre position actuelle',
      bandAll: 'TOUS LES CADRES',
      ribbonRecommended: 'RECOMMANDÉ',
      ribbonInProgressTemplate: 'EN COURS · {n} %',
      badgeAlreadyThere: 'DÉJÀ ATTEINT',
      badgeAmbitious: 'AMBITIEUX',
      ambitiousNote: 'Deux niveaux d’avance — commencez par un cadre « défini ».',
      metricDeliverables: ' livrables',
      metricPhases: ' phases',
      metricActivities: ' activités',
      metricMonths: ' mois',
      emptyInDomainTitle: 'Aucun cadre dans ce domaine',
      emptyInDomainBody:
        'L’équipe de contenu en prépare d’autres. En attendant, explorez les autres domaines — ou demandez au Coach quoi prioriser.',
      emptyNoneAtAllTitle: 'Aucun cadre publié pour l’instant',
      emptyNoneAtAllBody:
        'L’équipe de contenu finalise la première série. Nous mettrons un mot ici dès qu’un cadre sera disponible.',
      emptyClearFilter: 'Effacer le filtre',
      errorGeneric:
        'Impossible de charger la bibliothèque pour le moment. Rechargez la page ou réessayez dans un instant.',
    },
    domain: {
      D1_strategy_org: 'D1 — Stratégie & Organisation',
      D2_external_counsel: 'D2 — Conseils externes',
      D3_legal_ops_tech: 'D3 — Legal Ops & Tech',
      D4_team_dev: 'D4 — Développement d’équipe',
      D5_kpis_performance: 'D5 — KPIs & Performance',
      D6_budget_finance: 'D6 — Budget & Finances',
      D7_stakeholder_comms: 'D7 — Communication parties prenantes',
      D8_governance_risk: 'D8 — Gouvernance & Risque',
    },
    maturity: {
      initial: 'initial',
      developing: 'en dév.',
      established: 'défini',
      advanced: 'maîtrisé',
      optimized: 'optimisé',
    },
    duration: {
      lt_1_month: '< 1 mois',
      '1_3_months': '1–3 mois',
      '3_6_months': '3–6 mois',
      '6_9_months': '6–9 mois',
      '9_12_months': '9–12 mois',
      '12_18_months': '12–18 mois',
      '18_24_months': '18–24 mois',
    },
    workspace: {
      activitiesLabel: 'ACTIVITÉS',
      askCoachCta: 'Demander au coach',
      askCoachAbout: 'Demander au coach à ce sujet',
      askCoachOpening: 'Ouverture du coach…',
      askCoachError: 'Impossible d’ouvrir le coach. Réessayez.',
      tabFocus: 'Focus',
      tabTimeline: 'Calendrier',
      tabDeliverables: 'Livrables',
      doThisNext: 'À FAIRE MAINTENANT',
      openActivity: 'Ouvrir l’activité',
      allDoneTitle: 'Tout est terminé.',
      allDoneBody:
        'Toutes les activités de ce cadre sont terminées. Rendez-vous sur Livrables pour confirmer votre progression de maturité quand vous êtes prêt.',
      nothingReadyTitle: 'Rien n’est prêt pour l’instant',
      nothingReadyBody:
        'Chaque activité non terminée attend une dépendance. Débloquez un prérequis, ou révisez le plan.',
      driftHeader: 'Une ou plusieurs activités dépassent la fenêtre prévue',
      driftPlannedTemplate: 'devait se terminer le {date}',
      awaitingReviewLabel: 'EN ATTENTE DE VOTRE REVUE',
      readyLabel: 'PRÊT QUAND VOUS VOULEZ',
      blockedLabel: 'EN ATTENTE D’UNE AUTRE ACTIVITÉ',
      needsPrefix: 'nécessite',
      timelinePlaceholderTitle: 'Le calendrier arrive à la prochaine version',
      timelinePlaceholderBody:
        'Une vue Gantt mois par mois de chaque activité, avec regroupement par phase, marqueurs de dérive et liens de dépendance. Prévue pour la V1.',
      deliverablesPlaceholderTitle: 'L’onglet Livrables arrive à la prochaine version',
      deliverablesPlaceholderBody:
        'Une vue par livrable avec dépôt, validation et (V1.x) évaluation par le Coach. Les données sous-jacentes sont déjà saisies sur vos activités.',
      notFoundTitle: 'Instance introuvable',
      notFoundBody:
        'Cette instance a peut-être été supprimée, ou le lien est incorrect. Retournez à votre bibliothèque.',
      waiting: 'EN ATTENTE',
      markStarted: 'Marquer démarré',
      markComplete: 'Marquer terminé',
      reopen: 'Ré-ouvrir',
      closeDrawer: 'Fermer',
      state: {
        not_started: 'Non démarré',
        in_progress: 'En cours',
        awaiting_review: 'En attente',
        complete: 'Terminé',
      },
      durationLabel: 'DURÉE',
      dependsOnLabel: 'DÉPEND DE',
      dependsOnNothing: 'rien',
      stakeholdersLabel: 'PARTIES PRENANTES',
      referenceLabel: 'DOCUMENTS DE RÉFÉRENCE',
      producesDeliverableLabel: 'PRODUIT UN LIVRABLE',
      templateNote:
        'Remplissez-le puis téléversez-le — c’est ainsi que le livrable se construit.',
      logLabel: 'JOURNAL',
      logEmpty: 'Aucune note pour l’instant.',
      addNotePlaceholder: 'Ajouter une note…',
      addNoteSubmit: 'Ajouter la note',
      blockedBlockPrefix: 'En attente de',
      blockedBlockSuffix:
        'Vous pouvez démarrer quand même — le plan suppose simplement cet ordre.',
    },
    activation: {
      stepLabel: 'ÉTAPE 1 SUR 2',
      titleTemplate: 'Démarrer {name} ?',
      summaryDeliverables: 'LIVRABLES',
      summaryMaturity: 'MATURITÉ',
      summaryDuration: 'DURÉE',
      nameLabel: 'NOM DE L’INSTANCE',
      startDateLabel: 'DATE DE DÉBUT',
      startDateNote: 'Détermine le calendrier. Modifiable à tout moment.',
      adjustDurationsLabel: 'AJUSTER LES DURÉES',
      adjustDurationsNote:
        'Les valeurs par défaut viennent de l’auteur. Les vôtres les remplacent.',
      criticalPathNote:
        'Le chemin critique suppose que les activités sur des branches indépendantes avancent en parallèle. Revoyez l’estimation à la hausse si ce n’est pas réaliste pour votre équipe.',
      defaultTag: 'défaut',
      ackLabel:
        'J’ai pris connaissance des parties prenantes à mobiliser — dont le conseil, le CEO et le CFO.',
      cancel: 'Annuler',
      submit: 'Activer',
      submitting: 'Activation…',
      alreadyRunningTitle: 'Déjà en cours',
      alreadyRunningBody:
        'Ce cadre a déjà une instance en cours pour votre organisation. Reprenez où vous en étiez, ou annulez.',
      alreadyRunningContinue: 'Ouvrir l’instance en cours',
      errorTitle: 'Impossible de charger ce cadre',
      errorGeneric:
        'L’activation n’a pas abouti. Réessayez dans un instant, ou contactez le support si cela persiste.',
    },
    preview: {
      backToLibrary: 'Tous les cadres',
      notFoundTitle: 'Cadre introuvable',
      notFoundBody:
        'Ce cadre a peut-être été retiré, ou le lien est incorrect. Essayez la bibliothèque.',
      ctaStartFramework: 'Démarrer ce cadre',
      ctaResumeInstance: 'Ouvrir votre instance en cours',
      ctaAskCoach: 'Interroger le Coach',
      ctaFooterNote: 'Vous ajusterez les durées et la date de début à l’étape suivante.',
      whatThisMovesLabel: 'CE QUE CELA FAIT PROGRESSER',
      whatThisMovesBodyTemplate:
        'Vous êtes en {current} sur {domain}. Terminer ce cadre vous fait passer à {target} — dès que vous confirmez que les preuves le justifient.',
      whatThisMovesBodyNoTarget:
        'L’équipe de contenu n’a pas encore fixé de cible de maturité pour ce cadre. Son exécution reste utile ; la revendication de maturité sera précisée dès qu’une cible sera publiée.',
      currentUnknown: 'niveau inconnu',
      timeLabel: 'DURÉE',
      timeNote: 'ajustable activité par activité',
      timeUnknown: 'durée à définir',
      deliverablesEyebrow: 'CE QUE VOUS OBTENEZ',
      deliverablesTitleTemplate: '{n} livrables',
      showRubric: 'Ce qu’un bon livrable contient →',
      hideRubric: 'Masquer la grille d’évaluation',
      theWorkEyebrow: 'LE TRAVAIL',
      theWorkNote:
        'ouvrez n’importe quelle activité — tout est visible avant de vous engager',
      phaseLabel: 'PHASE',
      afterLabel: 'après',
      producesDeliverable: 'PRODUIT UN LIVRABLE',
      stakeholdersLabel: 'QUI SERA MOBILISÉ',
      stakeholdersBody:
        'Sur toutes les activités. Le temps des parties prenantes seniors (Conseil, CEO, CFO) est la première cause de blocage d’un cadre stratégique — vérifiez les disponibilités avant de démarrer.',
    },
  },
} satisfies StringTree;

const LOCALES: Record<Lang, typeof en> = { EN: en, FR: fr };

function get(tree: StringTree, key: string): string {
  const path = key.split('.');
  let node: string | StringTree = tree;
  for (const segment of path) {
    if (typeof node !== 'object' || !(segment in node)) {
      // Degrade gracefully: missing key logs a warning and returns the raw
      // key as its own label. Throwing blanks the whole page (React unwinds
      // the render tree), a disproportionate cost for a translation gap.
      console.warn(`[i18n] key not found: ${key}`);
      return key;
    }
    const next: string | StringTree | undefined = (node as StringTree)[segment];
    if (next === undefined) {
      console.warn(`[i18n] key not found: ${key}`);
      return key;
    }
    node = next;
  }
  if (typeof node !== 'string') {
    console.warn(`[i18n] key is not a leaf: ${key}`);
    return key;
  }
  return node;
}

function interp(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, key) =>
    key in values ? String(values[key]) : m,
  );
}

interface I18nApi {
  lang: Lang;
  setLang: (next: Lang) => void;
  t: (key: string) => string;
  /** Resolve a template key and substitute {placeholders}. */
  tt: (key: string, values: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nApi | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>('EN');
  const api = useMemo<I18nApi>(
    () => ({
      lang,
      setLang,
      t: (key) => get(LOCALES[lang], key),
      tt: (key, values) => interp(get(LOCALES[lang], key), values),
    }),
    [lang],
  );
  return <I18nContext.Provider value={api}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nApi {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}
